import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button, Spin } from "@arco-design/web-react";
import axios from "axios";

import { endpoints } from "../../api/endpoints";
import { User } from "../../models/Person";
import { UserAvatar } from "../../components/UserAvatar/UserAvatar";
import { routes } from "../../routes/routes";
import { IntakeShell } from "./components/IntakeShell";
import { IntakeFooterNav } from "./components/IntakeFooterNav";
import { useIntakeCopy } from "./hooks/useIntakeCopy";
import type { TFunction } from "i18next";
import { saveIntakeMatches } from "./services/intakeMatchStorage";
import { trackEvent } from "../../analytics/track";
import { getOrCreateSessionId, persistIntakeSession } from "./services/intakeSessionApi";
import { MatchScoringInput, ScoredMatch, scoreMentors } from "./services/matchScoring";
import { badgeLabel } from "./services/whyYouMatchCopy";
import { userToScoringMentor } from "./services/sessionBuilder";

type DisplayMatch = ScoredMatch & {
  userId: number;
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  role: string;
};

function MatchCard({
  match,
  whyLabel,
  learnLabel,
  t,
}: {
  match: DisplayMatch;
  whyLabel: string;
  learnLabel: string;
  t: TFunction;
}) {
  const isBestMatch =
    match.badge === "best_match" || match.badge === "soul_match";
  const badgeText = badgeLabel(match.badge, t);

  return (
    <article
      className={`intake-match-card${isBestMatch ? " intake-match-card--best" : ""}`}
    >
      {badgeText ? (
        <span
          className={`intake-match-card__badge${
            match.badge === "soul_match" ? " intake-match-card__badge--soul" : ""
          }`}
        >
          {badgeText}
        </span>
      ) : null}
      <div className="intake-match-card__photo-wrap">
        <UserAvatar
          avatarUrl={match.avatarUrl}
          firstName={match.firstName ?? match.mentor.name.split(" ")[0]}
          lastName={
            match.lastName ?? match.mentor.name.split(" ").slice(1).join(" ")
          }
          shape="square"
          alt={match.mentor.name}
          className="intake-match-card__photo"
        />
      </div>
      <p className="intake-match-card__compat">{match.compatibilityScore}% match</p>
      <h3 className="intake-match-card__name">{match.mentor.name}</h3>
      <p className="intake-match-card__role">{match.role}</p>
      <p className="intake-match-card__reason-label">{whyLabel}</p>
      <p className="intake-match-card__reason">{match.whyYouMatch}</p>
      <NavLink
        to={routes.MENTOR.replace(":id", String(match.userId))}
        state={{ fromIntake: true }}
      >
        <Button className="intake-match-card__learn-btn" size="large">
          {learnLabel}
        </Button>
      </NavLink>
    </article>
  );
}

type Props = {
  sessionInput: MatchScoringInput | null;
  onBack?: () => void;
};

export function IntakeMatchResults({ sessionInput, onBack }: Props) {
  const { t, progressLabel } = useIntakeCopy();
  const navigate = useNavigate();
  const [mentors, setMentors] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const trackedResultsRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const loadMentors = async () => {
      try {
        const response = await axios.get(endpoints.getIntakeMentors, {
          withCredentials: false,
        });
        const list: User[] = response.data?.mentors ?? [];
        if (isMounted) {
          setMentors(list);
          setLoadError(
            list.length === 0 ? t("intake.results.no_mentors") : null
          );
        }
      } catch (error) {
        console.error("[Intake] Failed to load mentors:", error);
        if (isMounted) {
          setLoadError(t("intake.results.load_error"));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadMentors();
    return () => {
      isMounted = false;
    };
  }, [t]);

  const displayMatches = useMemo((): DisplayMatch[] => {
    if (mentors.length === 0) return [];

    const scoringMentors = mentors.map(userToScoringMentor);
    const fallbackSession: MatchScoringInput = sessionInput ?? {
      sentimentScore: 0.5,
      sentimentMagnitude: 0.3,
      faceJoy: 0.5,
      faceSorrow: 0,
      faceAnger: 0,
      transcripts: [],
      journeyType: "corporate",
      role: "mentee",
      allAnswerMagnitudes: [],
    };

    const scored = scoreMentors(fallbackSession, scoringMentors).slice(0, 4);

    return scored.map((match) => {
      const user = mentors.find((m) => String(m.id) === match.mentor.id);
      return {
        ...match,
        userId: user?.id ?? Number(match.mentor.id),
        avatarUrl: user?.avatar_url,
        firstName: user?.name,
        lastName: user?.last_name,
        role: user?.shortDescription || match.mentor.title,
      };
    });
  }, [mentors, sessionInput]);

  useEffect(() => {
    if (displayMatches.length === 0 || trackedResultsRef.current) return;
    trackedResultsRef.current = true;
    saveIntakeMatches(displayMatches);
    const topBadge = displayMatches[0]?.badge;
    trackEvent("match_result_view", {
      match_count: displayMatches.length,
      top_badge: topBadge || "none",
    });
    trackEvent("intake_complete", {
      match_count: displayMatches.length,
      soul_match: topBadge === "soul_match",
    });
    void persistIntakeSession({
      sessionId: getOrCreateSessionId(),
      sessionInputJson: sessionInput ?? undefined,
      scoredMatchesJson: displayMatches,
      soulMatch: topBadge === "soul_match",
      completed: true,
    });
  }, [displayMatches, sessionInput]);

  return (
    <IntakeShell
      progressStep={5}
      progressTotal={5}
      progressLabel={progressLabel(5, 5)}
      footer={
        onBack ? (
          <IntakeFooterNav
            onBack={onBack}
            backLabel={t("intake.back")}
            primaryLabel={t("intake.results.browse_community")}
            onPrimary={() => navigate(routes.SEARCH)}
          />
        ) : undefined
      }
    >
      <h1 className="intake-flow__headline">{t("intake.results.headline")}</h1>
      <p className="intake-flow__subtext intake-flow__subtext--center">
        {t("intake.results.subtext")}
      </p>

      <NavLink className="intake-results__browse-btn" to={routes.SEARCH}>
        {t("intake.results.browse_community")}
      </NavLink>

      {isLoading ? (
        <Spin size={32} style={{ margin: "40px auto" }} />
      ) : loadError ? (
        <p className="intake-flow__subtext">{loadError}</p>
      ) : (
        <div className="intake-results__row">
          {displayMatches.map((match) => (
            <MatchCard
              key={match.userId}
              match={match}
              whyLabel={t("intake.results.why_match")}
              learnLabel={t("intake.results.learn_more")}
              t={t}
            />
          ))}
        </div>
      )}
    </IntakeShell>
  );
}
